import React, { useState } from "react";
import * as RadioGroup from "@radix-ui/react-radio-group";
import * as Select from "@radix-ui/react-select";
import countriesData from "../../../assets/countries.json";
import styles from "./AffiliateApplicationForm.module.css";

interface AffiliateApplicationForm {
  legal_name: string;
  business_name?: string;
  tax_id?: string;
  payout_method: "bank_transfer" | "paypal";
  // Bank Transfer fields
  bank_account_holder: string;
  bank_name: string;
  account_number: string;
  iban?: string;
  // PayPal field
  paypal_email: string;
  // Contact information
  address: string;
  city: string;
  country: string;
  postal_code: string;
  phone: string;
  notes?: string;
}

interface AffiliateApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: AffiliateApplicationForm) => Promise<void>;
  isSubmitting: boolean;
}

const AffiliateApplicationForm: React.FC<AffiliateApplicationFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [applicationForm, setApplicationForm] =
    useState<AffiliateApplicationForm>({
      legal_name: "",
      business_name: "",
      tax_id: "",
      payout_method: "bank_transfer",
      // Bank Transfer fields
      bank_account_holder: "",
      bank_name: "",
      account_number: "",
      iban: "",
      // PayPal field
      paypal_email: "",
      // Contact information
      address: "",
      city: "",
      country: "Saudi Arabia",
      postal_code: "",
      phone: "",
      notes: "",
    });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setApplicationForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(applicationForm);
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Affiliate Application</h3>
          <button
            onClick={onClose}
            className={styles.modalCloseButton}
            type="button"
          >
            X
          </button>
        </div>

        <form
          onSubmit={handleSubmitApplication}
          className={styles.affiliateForm}
        >
          {/* Personal Information Section */}
          <div className={styles.formSection}>
            <h4>Personal Information</h4>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label htmlFor="legal_name">Legal Name *</label>
                <input
                  type="text"
                  id="legal_name"
                  name="legal_name"
                  value={applicationForm.legal_name}
                  onChange={handleInputChange}
                  required
                  className={styles.formInput}
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="business_name">Business Name</label>
                <input
                  type="text"
                  id="business_name"
                  name="business_name"
                  value={applicationForm.business_name}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>
            </div>
          </div>

          {/* Payout Method Section */}
          <div className={styles.formSection}>
            <h4>Payout Method</h4>
            <RadioGroup.Root
              value={applicationForm.payout_method}
              onValueChange={(value: "bank_transfer" | "paypal") =>
                setApplicationForm((prev) => ({ ...prev, payout_method: value }))
              }
              className={styles.payoutMethodGroup}
            >
              {/* Bank Transfer Option */}
              <div
                className={`${styles.payoutOption} ${
                  applicationForm.payout_method === "bank_transfer"
                    ? styles.selected
                    : styles.unselected
                }`}
              >
                <RadioGroup.Item
                  value="bank_transfer"
                  id="bank_transfer"
                  className={`${styles.radioItem} ${
                    applicationForm.payout_method === "bank_transfer"
                      ? styles.selected
                      : ""
                  }`}
                >
                  <RadioGroup.Indicator className={styles.radioIndicator}>
                    <div className={styles.radioDot} />
                  </RadioGroup.Indicator>
                </RadioGroup.Item>
                <label
                  htmlFor="bank_transfer"
                  className={`${styles.radioLabel} ${
                    applicationForm.payout_method === "bank_transfer"
                      ? styles.selected
                      : styles.unselected
                  }`}
                >
                  Bank Transfer
                </label>
              </div>

              {/* PayPal Option */}
              <div
                className={`${styles.payoutOption} ${
                  applicationForm.payout_method === "paypal"
                    ? styles.selected
                    : styles.unselected
                }`}
              >
                <RadioGroup.Item
                  value="paypal"
                  id="paypal"
                  className={`${styles.radioItem} ${
                    applicationForm.payout_method === "paypal"
                      ? styles.selected
                      : ""
                  }`}
                >
                  <RadioGroup.Indicator className={styles.radioIndicator}>
                    <div className={styles.radioDot} />
                  </RadioGroup.Indicator>
                </RadioGroup.Item>
                <label
                  htmlFor="paypal"
                  className={`${styles.radioLabel} ${
                    applicationForm.payout_method === "paypal"
                      ? styles.selected
                      : styles.unselected
                  }`}
                >
                  PayPal
                </label>
              </div>
            </RadioGroup.Root>

            {/* Bank Transfer Fields */}
            {applicationForm.payout_method === "bank_transfer" && (
              <div className={styles.formSubSection}>
                <h5>Bank Transfer Details</h5>
                <div className={styles.formSubGrid}>
                  <div className={styles.formSubGroup}>
                    <div className={styles.formGroup}>
                      <label htmlFor="bank_account_holder">
                        Account Holder Name *
                      </label>
                      <input
                        type="text"
                        id="bank_account_holder"
                        name="bank_account_holder"
                        value={applicationForm.bank_account_holder}
                        onChange={handleInputChange}
                        required={applicationForm.payout_method === "bank_transfer"}
                        className={styles.formInput}
                      />
                    </div>
                    <div className={styles.formGroup}>
                      <label htmlFor="bank_name">Bank Name *</label>
                      <input
                        type="text"
                        id="bank_name"
                        name="bank_name"
                        value={applicationForm.bank_name}
                        onChange={handleInputChange}
                        required={applicationForm.payout_method === "bank_transfer"}
                        className={styles.formInput}
                      />
                    </div>
                    </div>
                    <div className={styles.formSubGroup}>
                  <div className={styles.formGroup}>
                    <label htmlFor="account_number">Account Number *</label>
                    <input
                      type="text"
                      id="account_number"
                      name="account_number"
                      value={applicationForm.account_number}
                      onChange={handleInputChange}
                      required={applicationForm.payout_method === "bank_transfer"}
                      className={styles.formInput}
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label htmlFor="iban">IBAN</label>
                    <input
                      type="text"
                      id="iban"
                      name="iban"
                      value={applicationForm.iban}
                      onChange={handleInputChange}
                      placeholder="SA..."
                      className={styles.formInput}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* PayPal Field */}
            {applicationForm.payout_method === "paypal" && (
              <div className={styles.formSection}>
                <h5>PayPal Details</h5>
                <div className={styles.formGroup}>
                  <label htmlFor="paypal_email">PayPal Email Address *</label>
                  <input
                    type="email"
                    id="paypal_email"
                    name="paypal_email"
                    value={applicationForm.paypal_email}
                    onChange={handleInputChange}
                    required={applicationForm.payout_method === "paypal"}
                    placeholder="your-email@example.com"
                    className={styles.formInput}
                  />
                  <p className={styles.helperText}>
                    Enter the email address associated with your PayPal account
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Contact Information Section */}
          <div className={styles.formSection}>
            <h4>Contact Information</h4>
            <div className={styles.formSubSection}>
              <div className={styles.formSubGroup}>
                <div className={styles.formGroup}>
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={applicationForm.phone}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
                </div>
              

                <div className={styles.formGroup}>
                <label>Country *</label>
                <Select.Root
                  value={applicationForm.country}
                  onValueChange={(value) =>
                    setApplicationForm((prev) => ({ ...prev, country: value }))
                  }
                >
                  <Select.Trigger className={styles.formSelect}>
                    <Select.Value placeholder="Select a country" />
                    <Select.Icon style={{ marginLeft: "8px", fontSize: "12px" }}>
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content
                      position="popper"
                      side="bottom"
                      align="start"
                      sideOffset={4}
                      className={styles.selectContent}
                    >
                      
                      <Select.Viewport className={styles.selectViewport}>
                        {countriesData.map((country) => (
                          <Select.Item
                            key={country.code}
                            value={country.name}
                            className={styles.selectItem}
                          >
                            <Select.ItemText>{country.name}</Select.ItemText>
                            <Select.ItemIndicator className={styles.selectItemIndicator}>
                              ✓
                            </Select.ItemIndicator>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                      
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
                </div>
                
              </div>
              <div className={styles.formSubGroup}>
                <div className={styles.formGroup}>
                  <label htmlFor="city">City *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={applicationForm.city}
                    onChange={handleInputChange}
                    required
                    className={styles.formInput}
                  />
              
              <div className={styles.formGroup}>
                <label htmlFor="postal_code">Postal Code</label>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  value={applicationForm.postal_code}
                  onChange={handleInputChange}
                  className={styles.formInput}
                />
              </div>
              </div>
              </div>
            </div>
            <div className={styles.formSubGroup}>
              <div className={styles.formGroup}>
              <label htmlFor="address">Address *</label>
              <textarea
                id="address"
                name="address"
                value={applicationForm.address}
                onChange={handleInputChange}
                required
                rows={3}
                className={styles.formTextarea}
              />
            </div>
            </div>
          </div>

          {/* Additional Notes Section */}
          <div className={styles.formSection}>
            <h4>Additional Notes</h4>
            <div className={styles.formGroup}>
              <label htmlFor="notes">
                Tell us about your marketing channels, audience, or any other
                relevant information
              </label>
              <textarea
                id="notes"
                name="notes"
                value={applicationForm.notes}
                onChange={handleInputChange}
                rows={4}
                placeholder="e.g., I have a YouTube channel with 10k subscribers focused on tech reviews, or I run a blog about financial advice..."
                className={styles.formTextarea}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={styles.submitButton}
            >
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AffiliateApplicationForm;
